import { useEffect } from "react";

import { useQueries, useQueryClient } from "@tanstack/react-query";

import { getCalculatedPorkFootprintByYear } from "services";

import { getActiveToken, getEnvironment, invalidateAndRefecthQueries, shouldRetryQueryError, useGetCarbonProgramByFarmId, useUserContext } from "utils";

import { FOOTPRINTING_PIGLET_CALCULATED_FOOTPRINT, FOOTPRINTING_PORK_CALCULATED_FOOTPRINT } from "consts";

export const useGetFootprintingCalculatedPorkFootprints = (farmId?: string, years: number[] = [], { enabled = true } = {}) => {
  const isAdmin = getEnvironment().admin;
  const queryClient = useQueryClient();
  const { user } = useUserContext();
  const { data: carbonProgram } = useGetCarbonProgramByFarmId(farmId);
  const token = getActiveToken();
  const yearsKey = years.join(",");

  // Pork footprints read piglet-derived data from redis via a VVVO number, so pork must recalculate
  // whenever its sibling piglet footprint does. Watch the cache for a successful fetch of one of our
  // own years' piglet queries, wherever it's triggered from, and refetch the matching pork footprint.
  useEffect(() => {
    if (!farmId) return undefined;

    return queryClient.getQueryCache().subscribe((event) => {
      if (event.type !== "updated" || event.action.type !== "success" || event.action.manual) return;

      const [key, eventFarmId, eventYear, eventIsAdmin] = event.query.queryKey;
      if (key !== FOOTPRINTING_PIGLET_CALCULATED_FOOTPRINT) return;
      if (eventFarmId !== farmId || eventIsAdmin !== isAdmin || !years.includes(eventYear as number)) return;

      invalidateAndRefecthQueries(queryClient, [[FOOTPRINTING_PORK_CALCULATED_FOOTPRINT, eventFarmId, eventYear, eventIsAdmin]]);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- yearsKey is a stable stand-in for years, whose array identity changes every render
  }, [queryClient, farmId, isAdmin, yearsKey]);

  return useQueries({
    queries: years.map((year) => ({
      queryKey: [FOOTPRINTING_PORK_CALCULATED_FOOTPRINT, farmId, year, isAdmin],
      queryFn: () => getCalculatedPorkFootprintByYear(year),
      enabled: enabled && !!farmId && !!year && !!user && !!token && !!carbonProgram?.platformFeatures?.footprinting?.pork,
      queryClient,
      gcTime: 60000,
      staleTime: 0,
      retry: shouldRetryQueryError(),
    })),
  });
};
